import Badge from '@mui/material/Badge';
import IconButton from '@mui/material/IconButton';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import { CircularProgress, ListItemText, MenuItem, Popover } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { useNotificationCore } from '../../core/modules/notification';
import { notificationKey } from '../../core/modules/notification/store/reducer';
import { NotificationClass, NotificationClassComment, NotificationForumComment, NotificationAnnouncement } from '../../core/interfaces/notification.interface';
import { useNavigationCore } from '../../core/router/navigation';
import ClassCommentNotification from './ClassCommentNotification';
import ClassNotification from './ClassNotification';
import ForumCommentNotification from './ForumCommentNotification';
import AnnouncementNotificationMenuItem from './AnnouncementNotificationMenuItem';
import NotificationErrorBoundary from '../components/notifications/NotificationErrorBoundary';
import useOpenNotification from '../components/notifications/useOpenNotification';

const NotificationsMenu = () => {
  const { unreadNotifications, unreadLoading, unreadLoaded, getUnreadNotifications } = useNotificationCore();
  const { goToNotifications } = useNavigationCore();

  const [notificationMenu, setNotificationMenu] = useState(null);

  const notificationMenuClick = (e: any) => {
    setNotificationMenu(e.currentTarget);
    // Siempre refrescar al abrir: antes solo se pedía al cambiar de página, así que si
    // esa llamada fallaba o aún no llegaba, el menú se veía vacío.
    getUnreadNotifications();
  };

  const menuClose = useCallback(() => {
    setNotificationMenu(null);
  }, []);

  const openNotification = useOpenNotification(menuClose);

  const hasNotifications = useMemo(() => unreadNotifications.length > 0, [unreadNotifications.length]);

  return (
    <>
      <IconButton className="w-40 h-40 mx-2" onClick={notificationMenuClick} size="large">
        <Badge color="secondary" variant="dot" invisible={!hasNotifications}>
          <NotificationsNoneOutlinedIcon />
        </Badge>
      </IconButton>

      <Popover
        open={Boolean(notificationMenu)}
        anchorEl={notificationMenu}
        onClose={menuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        classes={{ paper: 'py-8' }}
      >
        {hasNotifications ? (
          <>
            {unreadNotifications.map((notification) => (
              <NotificationErrorBoundary key={notificationKey(notification)}>
                <MenuItem
                  style={{ borderBottom: '1px solid #c4c4c4' }}
                  onClick={() => openNotification(notification)}
                >
                  <div style={{ display: 'flex', alignItems: 'center', maxWidth: '250px' }}>
                    {notification.type === 'notification_announcement' ? (
                      <AnnouncementNotificationMenuItem notification={notification as NotificationAnnouncement} />
                    ) : null}
                    {notification.type === 'classComment' ? (
                      <ClassCommentNotification notification={notification as NotificationClassComment} />
                    ) : null}
                    {notification.type === 'forumComment' ? (
                      <ForumCommentNotification notification={notification as NotificationForumComment} />
                    ) : null}
                    {notification.type === 'class' ? (
                      <ClassNotification notification={notification as NotificationClass} />
                    ) : null}
                  </div>
                </MenuItem>
              </NotificationErrorBoundary>
            ))}
          </>
        ) : unreadLoading || !unreadLoaded ? (
          <MenuItem disabled style={{ borderBottom: '1px solid #c4c4c4', justifyContent: 'center', opacity: 1 }}>
            <CircularProgress size={20} sx={{ color: '#7c3aed' }} />
            <ListItemText style={{ textAlign: 'center', flex: 'none', marginLeft: 12 }} primary="Cargando..." />
          </MenuItem>
        ) : (
          <MenuItem style={{ borderBottom: '1px solid #c4c4c4' }}>
            <ListItemText style={{ textAlign: 'center' }} primary="No hay notificaciones" />
          </MenuItem>
        )}
        <MenuItem onClick={() => {
          menuClose();
          goToNotifications();
        }}>
          <ListItemText style={{ textAlign: 'center' }} primary="Ver todas las notificaciones" />
        </MenuItem>
      </Popover>
    </>
  );
};

export default NotificationsMenu;
